import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Rss } from "lucide-react";
import SEOHead, { organizationSchema, breadcrumbSchema } from "@/components/SEOHead";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import { SITE } from "@/lib/seoData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogIndex() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/blog/posts`, { withCredentials: false })
      .then((res) => setPosts(res.data || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEOHead
        title="BreakItDown Blog — Productivity, Focus & AI Task Management"
        description="The BreakItDown blog: articles on productivity systems, ADHD focus, AI task management, remote work, and workflow design."
        path="/blog"
        schemas={[
          organizationSchema(),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Blog", path: "/blog" },
          ]),
        ]}
      />
      <MarketingHeader />
      <main className="flex-1 max-w-3xl mx-auto px-5 md:px-10 py-12 md:py-16 w-full">
        <div className="flex items-start justify-between gap-4 mb-3">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">Blog</p>
          <a
            href={`${SITE.domain}/api/blog/feed.xml`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.12em] text-zinc-500 hover:text-[#002FA7] transition-colors"
            data-testid="blog-rss-link"
            title="Subscribe via RSS"
          >
            <Rss className="w-3.5 h-3.5" strokeWidth={2} />
            RSS
          </a>
        </div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900 leading-tight">
          On productivity, focus, and what really helps you ship.
        </h1>

        <div className="mt-12 space-y-8">
          {loading && <p className="text-sm text-zinc-400">Loading…</p>}
          {!loading && posts.length === 0 && (
            <div className="border border-zinc-200 p-8 text-center">
              <p className="text-sm text-zinc-500 mb-4">Articles are on the way.</p>
              <Link to="/" className="text-sm text-[#002FA7] underline">Try BreakItDown</Link>
            </div>
          )}
          {posts.map((p) => (
            <article key={p.slug} className="border-b border-zinc-200 pb-8">
              <div className="text-xs font-mono text-zinc-500">{p.published_at?.slice(0, 10)}</div>
              <h2 className="text-xl font-semibold text-zinc-900 mt-2">
                <Link to={`/blog/${p.slug}`} className="hover:text-[#002FA7]">{p.title}</Link>
              </h2>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{p.excerpt}</p>
            </article>
          ))}
        </div>
      </main>
      <MarketingFooter />
    </div>
  );
}
